import React from 'react';
import {faDownload} from '@fortawesome/pro-solid-svg-icons';
import {Button, LinearProgress, Typography} from '@mui/material';
import Dialog, {
	DialogProps,
	WithDialogProps,
} from '@netx/core-components/Dialog';
import {getPreference} from '@netx/core-user-next/utils/getPreference';
import {userProfile} from '@netx/core-user/collections/singletons/userProfile';
import {i18n} from '@netx/core/i18n';

export interface DownloadOptions {
	description?: string;
	title?: string | React.ReactNode;
	disableEmailWhenDone?: boolean;
	onEmailWhenDone?: () => void;
	getUUID(): Promise<string>;
}

export interface DownloadDialogProps
	extends Omit<WithDialogProps<DownloadOptions>, 'title'> {
	completeUrl?: string;
	disableEmailWhenDone?: boolean;
	error?: string;
	emailWhenDoneLabel?: string;
	pendingApproval?: boolean;
	progress?: number;
	title?: string | React.ReactNode;
	uuid?: string;
	DialogProps?: Partial<DialogProps>;
}

export const DownloadDialog: React.FunctionComponent<
	React.PropsWithChildren<DownloadDialogProps>
> = ({
	completeUrl,
	description,
	disableEmailWhenDone,
	error,
	pendingApproval,
	emailWhenDoneLabel,
	open,
	progress,
	title,
	onClose,
	onEmailWhenDone,
	uuid,
	DialogProps,
}) => {
	return (
		<Dialog
			data-testid="download-dialog"
			error={error}
			icon={faDownload}
			disableContentPadding={!!error && !pendingApproval && !description}
			fullWidth
			open={open}
			onClose={pendingApproval ? onClose : undefined}
			title={
				title != null
					? title
					: pendingApproval
					? i18n.t('i18n.requestSubmitted')
					: i18n.t('i18n.downloading')
			}
			dialogActions={
				<>
					{!disableEmailWhenDone && !error && uuid ? (
						<Button
							onClick={onEmailWhenDone}
							color="primary"
							variant="outlined"
						>
							{emailWhenDoneLabel != null
								? emailWhenDoneLabel
								: i18n.t('i18n.emailWhenDone')}
						</Button>
					) : null}
					{error || pendingApproval ? (
						<Button
							onClick={() => onClose?.({}, 'escapeKeyDown')}
							color="primary"
						>
							{i18n.t('i18n.close')}
						</Button>
					) : null}
				</>
			}
			{...DialogProps}
		>
			{pendingApproval ? (
				<div
					dangerouslySetInnerHTML={{
						__html: i18n.t('i18n.downloadApprovalMessage', {
							yourEmail: userProfile.get('email'),
							adminEmail: getPreference('image.downloadApprovalAdminEmail'),
						}),
					}}
					style={{marginTop: 16}}
				/>
			) : !error || description != null ? (
				<>
					{description != null ? (
						<Typography
							id="alert-dialog-slide-description"
							paragraph
							color="textSecondary"
							component="div"
							variant="body1"
						>
							{description}
						</Typography>
					) : null}
					{!error ? (
						<LinearProgress
							aria-label={i18n.t('i18n.downloadProgress')}
							variant={
								progress == null || (progress === 100 && completeUrl == null)
									? 'indeterminate'
									: 'determinate'
							}
							value={progress}
							sx={{
								mt: 2,
							}}
						/>
					) : null}
				</>
			) : null}
		</Dialog>
	);
};

export default DownloadDialog;
